import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Textarea } from '@/shared/components/ui/textarea';
import { Badge } from '@/shared/components/ui/badge';
import { Switch } from '@/shared/components/ui/switch';
import { toast } from 'sonner';
import {
  Send,
  Settings,
  Users,
  Bell,
  Link as LinkIcon,
  Copy,
  Eye,
  EyeOff,
  Plus,
  Trash2,
  CheckCircle2,
  AlertTriangle,
  Info,
  XCircle,
  Loader2,
  TestTube,
  Zap,
  GitBranch,
  BookOpen,
  ExternalLink } from
'lucide-react';

const API_URL = API_BASE;

// Default config structure
const defaultTelegramConfig = {
  enabled: true,
  botToken: '',
  botUsername: '@EzERPBot',
  webhookUrl: '',
  groups: [],
  alertTypes: [
  { type: 'critical', enabled: true, groups: [] },
  { type: 'warning', enabled: true, groups: [] },
  { type: 'info', enabled: true, groups: [] },
  { type: 'success', enabled: false, groups: [] }]

};

export const TelegramSettings = () => {const { t } = useTranslation("comms");
  const [config, setConfig] = useState(defaultTelegramConfig);
  const [loading, setLoading] = useState(true);
  const [showToken, setShowToken] = useState(false);
  const [telegramStatus, setTelegramStatus] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState('aiogram');
  const [testMessage, setTestMessage] = useState('');
  const [testChatId, setTestChatId] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isTesting, setIsTesting] = useState(false);

  // Fetch Telegram service status on mount
  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/telegram/status`);
      const data = await response.json();
      setTelegramStatus(data);
      if (data.default_method) {
        setSelectedMethod(data.default_method);
      }
      // Config is already in-memory in the backend, we keep using defaults
      // which get populated from the API status
    } catch (err) {
      console.error('Failed to fetch status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMethodChange = async (method) => {
    setSelectedMethod(method);
    try {
      await fetch(`${API_URL}/api/telegram/method`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ method })
      });
      toast.success(`Switched to ${method}`, {
        description: method === 'aiogram' ? 'Modern async library (recommended)' : 'Traditional handler-based library'
      });
    } catch (err) {
      toast.error('Failed to change method');
    }
  };

  const handleTestConnection = async () => {
    setIsTesting(true);
    try {
      const response = await fetch(`${API_URL}/api/telegram/test?method=${selectedMethod}`);
      const result = await response.json();

      if (result.status === 'success') {
        toast.success('Connection successful!', {
          description: result.message
        });
      } else if (result.status === 'simulation') {
        toast.info('Simulation Mode', {
          description: result.message
        });
      } else {
        toast.error('Connection failed', {
          description: result.message
        });
      }
    } catch (err) {
      toast.error('Connection test failed', {
        description: err.message
      });
    } finally {
      setIsTesting(false);
    }
  };

  const handleSendTestMessage = async () => {
    if (!testChatId || !testMessage) {
      toast.error('Please enter both Chat ID and message');
      return;
    }

    setIsSending(true);
    try {
      const response = await fetch(`${API_URL}/api/telegram/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chat_id: testChatId,
          message: testMessage,
          method: selectedMethod
        })
      });
      const result = await response.json();

      if (result.status === 'simulated') {
        toast.success('Message sent (SIMULATION)', {
          description: `ID: ${result.external_id} via ${result.method}`
        });
      } else if (result.status === 'sent') {
        toast.success('Message sent successfully!', {
          description: `ID: ${result.external_id} via ${result.method}`
        });
      } else {
        toast.error('Failed to send message', {
          description: result.error || 'Unknown error'
        });
      }
    } catch (err) {
      toast.error('Failed to send test message', {
        description: err.message
      });
    } finally {
      setIsSending(false);
    }
  };

  const alertTypeIcons = {
    critical: <XCircle className="h-4 w-4 text-red-600" />,
    warning: <AlertTriangle className="h-4 w-4 text-amber-600" />,
    info: <Info className="h-4 w-4 text-blue-600" />,
    success: <CheckCircle2 className="h-4 w-4 text-emerald-600" />
  };

  const methodsInfo = telegramStatus?.methods_info || {
    aiogram: { name: 'Aiogram', description: 'Modern async library', pros: [], cons: [] },
    'python-telegram-bot': { name: 'Python Telegram Bot', description: 'Traditional library', pros: [], cons: [] }
  };

  return (
    <div className="space-y-6" data-testid="communications-telegram">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk'] flex items-center gap-3">
            <Send className="h-8 w-8 text-blue-600" />{t("telegramSettings", "Telegram Settings")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("configureTelegramBot", "Configure Telegram bot for internal alerts and notifications")}

          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">{t("channelStatus", "Channel Status")}</span>
          <Switch checked={config.enabled} />
          {telegramStatus?.simulation_mode ?
          <Badge className="bg-amber-100 text-amber-700">{t("simulationMode", "Simulation Mode")}</Badge> :
          telegramStatus?.configured ?
          <Badge className="bg-emerald-100 text-emerald-700">{t("connected", "Connected")}</Badge> :

          <Badge className="bg-gray-100 text-gray-700">{t("notConfigured", "Not Configured")}</Badge>
          }
        </div>
      </div>

      {/* Method Toggle - NEW */}
      <Card className="card-elevated border-2 border-blue-200 bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-950/20 dark:to-indigo-950/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <GitBranch className="h-5 w-5 text-blue-600" />{t("libraryMethod", "Library Method")}

            <Badge className="ml-2 bg-blue-100 text-blue-700">{t("toggle", "Toggle")}</Badge>
          </CardTitle>
          <CardDescription>{t("chooseWhichTelegram", "Choose which Telegram library to use. Both are free and open-source.")}

          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-2">
            {/* Aiogram Option */}
            <div
              className={`relative p-4 rounded-lg border-2 cursor-pointer transition-all ${
              selectedMethod === 'aiogram' ?
              'border-blue-500 bg-blue-50 dark:bg-blue-950/30' :
              'border-border hover:border-blue-300'}`
              }
              onClick={() => handleMethodChange('aiogram')}>
              
              {selectedMethod === 'aiogram' &&
              <div className="absolute -top-2 -right-2">
                  <Badge className="bg-blue-600 text-white">{t("active", "Active")}</Badge>
                </div>
              }
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Zap className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-semibold">{t("aiogram", "Aiogram")}</p>
                  <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700">{t("recommended", "Recommended")}</Badge>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                {methodsInfo.aiogram?.description || 'Modern async library with router-based architecture'}
              </p>
              <div className="space-y-1">
                {(methodsInfo.aiogram?.pros || ['Async by design', 'Router-based', 'Scalable']).map((pro, i) =>
                <div key={i} className="flex items-center gap-2 text-xs text-emerald-600">
                    <CheckCircle2 className="h-3 w-3" />
                    {pro}
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-2 italic">{t("bestForProduction", "Best for: Production, high-traffic, complex workflows")}

              </p>
            </div>

            {/* Python-telegram-bot Option */}
            <div
              className={`relative p-4 rounded-lg border-2 cursor-pointer transition-all ${
              selectedMethod === 'python-telegram-bot' ?
              'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/30' :
              'border-border hover:border-indigo-300'}`
              }
              onClick={() => handleMethodChange('python-telegram-bot')}>
              
              {selectedMethod === 'python-telegram-bot' &&
              <div className="absolute -top-2 -right-2">
                  <Badge className="bg-indigo-600 text-white">{t("active", "Active")}</Badge>
                </div>
              }
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-indigo-500/10">
                  <BookOpen className="h-5 w-5 text-indigo-600" />
                </div>
                <div>
                  <p className="font-semibold">{t("pythonTelegramBot", "Python Telegram Bot")}</p>
                  <Badge variant="outline" className="text-xs">{t("traditional", "Traditional")}</Badge>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                {methodsInfo['python-telegram-bot']?.description || 'Traditional handler-based library'}
              </p>
              <div className="space-y-1">
                {(methodsInfo['python-telegram-bot']?.pros || ['Well documented', 'Large community', 'Simple API']).map((pro, i) =>
                <div key={i} className="flex items-center gap-2 text-xs text-indigo-600">
                    <CheckCircle2 className="h-3 w-3" />
                    {pro}
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-2 italic">{t("bestForSimple", "Best for: Simple bots, learning, quick prototypes")}

              </p>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between">
            <p className="text-sm text-muted-foreground">{t("currentlyUsing", "Currently using:")}
              <span className="font-medium text-foreground">{selectedMethod}</span>
            </p>
            <Button variant="outline" size="sm" onClick={handleTestConnection} disabled={isTesting}>
              {isTesting ?
              <Loader2 className="h-4 w-4 mr-2 animate-spin" /> :

              <CheckCircle2 className="h-4 w-4 mr-2" />
              }{t("testConnection", "Test Connection")}

            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Bot Configuration */}
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-muted-foreground" />{t("botConfiguration", "Bot Configuration")}

          </CardTitle>
          <CardDescription>{t("configureYourTelegram", "Configure your Telegram bot credentials. Create a bot via @BotFather on Telegram.")}

          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200">
            <p className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">{t("quickSetup", "Quick Setup:")}</p>
            <ol className="text-sm text-blue-700 dark:text-blue-300 space-y-1 list-decimal list-inside">
              <li>{t("openTelegramAnd", "Open Telegram and search for")}<span className="font-mono">{t("botfather", "@BotFather")}</span></li>
              <li>{t("send", "Send")}<span className="font-mono">{t("newbot", "/newbot")}</span>{t("andFollowPrompts", "and follow prompts")}</li>
              <li>{t("copyTheApi", "Copy the API token provided")}</li>
              <li>{t("addTo", "Add to")}<span className="font-mono">{t("backendenv", "backend/.env")}</span>: <span className="font-mono">{t("telegrambottokenyourtoken", "TELEGRAM_BOT_TOKEN=your_token")}</span></li>
            </ol>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("botToken", "Bot Token")}</label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    type={showToken ? 'text' : 'password'}
                    value={config.botToken}
                    placeholder="123456789:ABCdefGHIjklMNOpqrSTUvwxYZ"
                    className="pr-20 font-mono text-sm" />
                  
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-8 top-0 h-full"
                    onClick={() => setShowToken(!showToken)}>
                    
                    {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full">
                    
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">{t("getThisFrom", "Get this from @BotFather after creating your bot")}

              </p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("botUsername", "Bot Username")}</label>
              <Input
                value={config.botUsername}
                placeholder="@YourBotName"
                className="font-mono" />
              
              <p className="text-xs text-muted-foreground">{t("theUsernameOf", "The @username of your Telegram bot")}

              </p>
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">{t("webhookUrl", "Webhook URL")}</label>
            <div className="flex gap-2">
              <Input
                value={config.webhookUrl}
                placeholder="https://api.yourdomain.com/webhooks/telegram"
                className="font-mono text-sm" />
              
              <Button variant="outline">
                <LinkIcon className="h-4 w-4 mr-2" />{t("test", "Test")}

              </Button>
            </div>
            <p className="text-xs text-muted-foreground">{t("endpointWhereTelegram", "Endpoint where Telegram will send incoming updates")}

            </p>
          </div>
        </CardContent>
      </Card>

      {/* Group Management */}
      <Card className="card-elevated">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-muted-foreground" />{t("notificationGroups", "Notification Groups")}

            </CardTitle>
            <CardDescription>{t("manageTelegramGroups", "Manage Telegram groups that receive system notifications")}

            </CardDescription>
          </div>
          <Button className="gap-2 bg-blue-600 hover:bg-blue-700">
            <Plus className="h-4 w-4" />{t("addGroup", "Add Group")}

          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {config.groups.map((group) =>
            <div key={group.id} className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-muted/30 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="h-10 w-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                    <Users className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium">{group.name}</p>
                    <p className="text-sm text-muted-foreground font-mono">{group.chatId}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <Badge variant="secondary">{group.members}{t("members", "members")}</Badge>
                  <Switch checked={group.enabled} />
                  <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-red-600">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Alert Types */}
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-muted-foreground" />{t("alertTypeRouting", "Alert Type Routing")}

          </CardTitle>
          <CardDescription>{t("configureWhichGroups", "Configure which groups receive different types of alerts")}

          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {config.alertTypes.map((alertType) =>
            <div key={alertType.type} className="flex items-center justify-between p-4 rounded-lg border border-border">
                <div className="flex items-center gap-3">
                  {alertTypeIcons[alertType.type]}
                  <div>
                    <p className="font-medium capitalize">{alertType.type}{t("alerts", "Alerts")}</p>
                    <p className="text-sm text-muted-foreground">
                      {alertType.groups.length}{t("groupsSubscribed", "group(s) subscribed")}
                  </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex gap-1">
                    {alertType.groups.map((groupId) => {
                    const group = config.groups.find((g) => g.id === groupId);
                    return group ?
                    <Badge key={groupId} variant="secondary" className="text-xs">
                          {group.name}
                        </Badge> :
                    null;
                  })}
                  </div>
                  <Switch checked={alertType.enabled} />
                  <Button variant="outline" size="sm">{t("configure", "Configure")}</Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Test Message */}
      <Card className="card-elevated border-dashed border-2 border-blue-300/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TestTube className="h-5 w-5 text-blue-600" />{t("sendTestMessage", "Send Test Message")}

            {telegramStatus?.simulation_mode &&
            <Badge className="ml-2 bg-amber-100 text-amber-700">{t("simulationMode", "SIMULATION MODE")}</Badge>
            }
            <Badge className="ml-2 bg-blue-100 text-blue-700">{t("via", "via")}{selectedMethod}</Badge>
          </CardTitle>
          <CardDescription>
            {telegramStatus?.simulation_mode ?
            'No bot token configured - messages will be simulated but not actually sent' :
            `Send a test message using ${selectedMethod} to verify your configuration`
            }
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("chatId", "Chat ID")}</label>
              <Input
                value={testChatId}
                onChange={(e) => setTestChatId(e.target.value)}
                placeholder="-1001234567890 or @username"
                className="font-mono" />
              
              <p className="text-xs text-muted-foreground">{t("groupChatId", "Group chat ID or username")}

              </p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("testMessage", "Test Message")}</label>
              <Textarea
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                placeholder="Hello from EzERP!"
                rows={2} />
              
            </div>
          </div>
          <Button
            onClick={handleSendTestMessage}
            disabled={isSending}
            className="bg-blue-600 hover:bg-blue-700">
            
            {isSending ?
            <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />{t("sending", "Sending...")}

            </> :

            <>
                <Send className="h-4 w-4 mr-2" />{t("sendTestMessage", "Send Test Message")}

            </>
            }
          </Button>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end gap-3">
        <Button variant="outline">{t("cancel", "Cancel")}</Button>
        <Button className="bg-blue-600 hover:bg-blue-700">{t("saveChanges", "Save Changes")}</Button>
      </div>
    </div>);

};